import { isSoftwareProject } from '../../utils/projectType';
import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import {
    getProject, getProjectMembers, getMilestones,
    getProjectInvitations,
    getProjectProgress, getStatusBreakdown,
    type Project, type ProjectMember, type Milestone,
    type ProjectProgress, type StatusCount,
} from '../../packages/api-client/pm-api';
import { exportProjectCSV } from '../../packages/api-client/export-api';
import { useAuth } from '../../packages/auth-client';
import { Skeleton, SkeletonCard } from '../../components/Skeleton';
import { AIPilot } from './AIPilot';
import { KanbanBoard } from './KanbanPage';
import { TimelineView } from './GanttPage';
import { PlanView } from './PlanPage';
import { StatusReportView } from './StatusReportPage';
import { AutomationRules } from '../../components/AutomationRules';
import { usePageTitle } from '../../hooks/usePageTitle';
import { formatDueDate } from '../../utils/date';
import { Download, Plus } from 'lucide-react';
import { Tabs } from '../../packages/ui';
import { useHasPermission } from '../../packages/permission-guard';
import { LegacyRedirect } from '../../routes/LegacyRedirect';
import { SECTIONS, RESTRICTED_SECTIONS, SECTION_HEADS, TOOL_CARDS, VIEWS, LEGACY_TABS, resolveSection } from './projectSections';
import { memberName } from './sections/dashboardParts';
import './ProjectDetailPage.css';
import { OverviewSection, useOverviewSection } from './sections/OverviewSection';
import { TasksSection, useTasksSection } from './sections/TasksSection';
import { AnalyticsSection } from './sections/ReportsSection';
import { ActivitySection } from './sections/ActivitySection';
import { MembersSection, InviteMemberModal, useMembersSection } from './sections/MembersSection';
import { DataLifecycle, ProjectSettings, TimeTool } from './sections/ToolsSection';

const STATUS_LABELS: Record<string, string> = {
    planning: 'Planning', active: 'Active', on_hold: 'On hold', completed: 'Completed', archived: 'Archived',
};

export function ProjectDetailPage() {
    const { id } = useParams<{ id: string }>();
    const [project, setProject] = useState<Project | null>(null);
    usePageTitle(project?.name ?? 'Project');
    const [members, setMembers] = useState<ProjectMember[]>([]);
    const [milestones, setMilestones] = useState<Milestone[]>([]);
    // Restriction is a user-level scope settable only by user-admins; the
    // restricted invite routes through the org-invite endpoint (users:create).
    const { user: authUser, isSuperAdmin, isRestricted } = useAuth();
    // Restricted users get exactly the pre-redesign clamp: Overview and the
    // Tasks list. Any other ?tab= (legacy or section) renders Overview and the
    // other sections are not offered. The standalone /projects/:id/<view>
    // routes are URL-blocked for them by RestrictedRedirect.
    const restricted = isRestricted();
    // Sections are URL state (?tab=<section>&view=<sub-view>): shareable,
    // refreshable, and each section/sub-view click is a history entry so
    // Back/Forward move between them. Pre-redesign ?tab= values are
    // redirected (replace) to their section below, keeping every other param.
    const [searchParams, setSearchParams] = useSearchParams();
    const rawTab = searchParams.get('tab');
    const legacy = rawTab ? LEGACY_TABS[rawTab] : undefined;
    const { tab, view } = resolveSection(rawTab, searchParams.get('view'), restricted);
    const sections = restricted ? RESTRICTED_SECTIONS : SECTIONS;
    const sectionLabel = sections.find(s => s.tab === tab)!.label;
    // UI gates only; the backend enforces both on every request.
    const canManageMembers = useHasPermission('project_members', 'create');
    const canCreateTasks = useHasPermission('tasks', 'create') && !restricted;
    // A section link keeps every param except a view the target can't use
    // (restricted users have no sub-views at all).
    const sectionLink = (t: string) => {
        const next = new URLSearchParams(searchParams);
        next.set('tab', t);
        if (restricted || !VIEWS[t]?.some(v => v.view === next.get('view'))) next.delete('view');
        return `?${next}`;
    };
    // A link to a section's specific sub-view (Overview's "Open project plan", tool cards).
    const viewLink = (t: string, v?: string) => {
        if (!v) return sectionLink(t);
        const next = new URLSearchParams(searchParams);
        next.set('tab', t); next.set('view', v);
        return `?${next}`;
    };
    const [loading, setLoading] = useState(true);
    // authUser / restricted are computed near the top (before the tab
    // derivation). canRestrict + taskRestricted are only needed lower down.
    const canRestrict = isSuperAdmin() || (authUser?.roles ?? []).includes('admin');
    // (for task-restricted users) no Milestones section on the overview.
    const taskRestricted = authUser?.is_task_restricted === true;
    const [progress, setProgress] = useState<ProjectProgress | null>(null);
    const [statusBreakdown, setStatusBreakdown] = useState<StatusCount[]>([]);

    // Section state lives in per-section hooks (sections/*.tsx) but is still
    // owned by this component, so it survives tab switches as before.
    const overview = useOverviewSection(id, setMilestones);
    const membersState = useMembersSection(id, canRestrict, setMembers);
    const { setInvitations, openInvite } = membersState;

    useEffect(() => {
        if (!id) return;
        Promise.all([getProject(id), getProjectMembers(id), getMilestones(id)]).then(([p, m, ms]) => {
            setProject(p); setMembers(m); setMilestones(ms); setLoading(false);
        });
        getProjectInvitations(id).then(setInvitations).catch(() => { /* non-admins may 403; hide the list */ });
        // Load analytics in parallel
        getProjectProgress(id).then(setProgress).catch(() => {});
        getStatusBreakdown(id).then(setStatusBreakdown).catch(() => {});
    // setInvitations is a stable useState setter from useMembersSection.
    }, [id, setInvitations]);

    // Called after the initial-load effect so effects keep their order.
    // The list fetches only while it is showing (the Board loads its own).
    const tasksState = useTasksSection(id, tab === 'Tasks' && view !== 'list' ? 'Board' : tab, searchParams, setSearchParams);

    if (legacy) return <LegacyRedirect to="/projects/:id" set={legacy.view ? { tab: legacy.tab, view: legacy.view } : { tab: legacy.tab }} />;

    if (loading) return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>
            <Skeleton width={120} height={14} />
            <Skeleton width={300} height={30} />
            <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
                {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} width={96} height={34} radius="var(--radius-md)" />)}
            </div>
            <SkeletonCard height={340} />
        </div>
    );
    if (!project) return <div>Project not found</div>;

    const repo = isSoftwareProject(project) ? repoLabel(project.repo_url) : null;
    const subViews = !restricted && VIEWS[tab];
    const head = !restricted ? SECTION_HEADS[tab] : undefined;
    const owner = members.find(m => m.user_id === project.owner_id);
    const newTask = () => {
        const next = new URLSearchParams(searchParams);
        next.set('tab', 'Tasks'); next.delete('view');
        setSearchParams(next);
        tasksState.setShowAddTask(true);
    };

    return (
        <div className="project-shell">
            <Link to="/projects" className="pd-back">← All projects</Link>
            <section className="pd-heading" aria-label="Project identity">
                <div className="pd-heading-main">
                    <span className="pd-eyebrow">Project dashboard</span>
                    <h1 className="page-title">{project.name}</h1>
                    {restricted && <p className="pd-muted">Your shared work in this project.</p>}
                    <div className="pd-identity">
                        <span className="pd-pill">{isSoftwareProject(project) ? 'Software project' : 'General project'}</span>
                        <span className={`status-badge status-${project.status}`}>{STATUS_LABELS[project.status]}</span>
                        {!restricted && owner && <span className="pd-pill">Owner: {memberName(owner)}</span>}
                        {!restricted && project.due_date && <span className="pd-pill">Target: {formatDueDate(project.due_date)}</span>}
                        <span className="pd-pill">{restricted ? 'Shared tasks only' : `Project access · ${members.length} ${members.length === 1 ? 'member' : 'members'}`}</span>
                    </div>
                </div>
                <div className="pd-heading-actions">
                    <button className="btn btn-ghost btn-sm" onClick={() => exportProjectCSV(project.id, project.name)} title="Export tasks as CSV">
                        <Download size={14} aria-hidden="true" /> Export CSV
                    </button>
                    {canManageMembers && <button className="btn btn-ghost btn-sm" onClick={openInvite}><Plus size={14} aria-hidden="true" /> Add member</button>}
                    {canCreateTasks && <button className="btn btn-primary btn-sm" onClick={newTask}><Plus size={14} aria-hidden="true" /> New task</button>}
                </div>
            </section>

            <Tabs label="Project sections" className="project-shell-tabs"
                items={sections.map(s => ({ to: sectionLink(s.tab), replaceQuery: true, active: s.tab === tab, label: s.label }))} />

            <section aria-label={sectionLabel}>
                {(head || (subViews && tab !== 'Tools')) && (
                    <div className="pd-filterbar">
                        {head && <div><h2>{head.title}</h2>{head.note && <p className="pd-muted">{head.note}</p>}</div>}
                        {subViews && tab !== 'Tools' && (
                            <Tabs label={`${sectionLabel} views`} className="pd-segmented"
                                items={subViews.map(v => ({ to: `?tab=${tab}&view=${v.view}`, label: v.label, active: v.view === view }))} />
                        )}
                    </div>
                )}
                {subViews && tab === 'Tools' && (
                    <nav aria-label={`${sectionLabel} views`} className="pd-feature-grid">
                        {subViews.map(v => (
                            <Link key={v.view} to={`?tab=Tools&view=${v.view}`} className="pd-feature-card" aria-current={v.view === view ? 'page' : undefined}>
                                <h3>{TOOL_CARDS[v.view].title} <span aria-hidden="true">↗</span></h3>
                                <p>{TOOL_CARDS[v.view].description}</p>
                            </Link>
                        ))}
                        <Link to={sectionLink('Reports')} className="pd-feature-card">
                            <h3>Reports &amp; exports <span aria-hidden="true">↗</span></h3>
                            <p>Analytics, burndown, status reporting, and CSV export.</p>
                        </Link>
                        <Link to={sectionLink('Activity')} className="pd-feature-card">
                            <h3>History &amp; snapshots <span aria-hidden="true">↗</span></h3>
                            <p>Follow changes over time in Activity and its time machine.</p>
                        </Link>
                        <Link to="/templates" className="pd-feature-card">
                            <h3>Templates &amp; marketplace <span aria-hidden="true">↗</span></h3>
                            <p>Reuse existing templates when adding work.</p>
                        </Link>
                    </nav>
                )}

                {tab === 'Overview' && (
                    <OverviewSection project={project} progress={progress} statusBreakdown={statusBreakdown}
                        milestones={milestones} members={members} restricted={restricted} taskRestricted={taskRestricted}
                        repo={repo} link={viewLink} state={overview} />
                )}

                {tab === 'Tasks' && view === 'list' && (
                    <TasksSection id={id} milestones={milestones} members={members} state={tasksState} />
                )}
                {tab === 'Tasks' && view === 'board' && id && (
                    <KanbanBoard projectId={id} projectColor={project.color || 'var(--color-primary)'} milestones={milestones} members={members} />
                )}

                {tab === 'Planning' && view === 'plan' && id && <PlanView projectId={id} />}
                {tab === 'Planning' && view === 'timeline' && id && <TimelineView projectId={id} />}

                {tab === 'Activity' && id && <ActivitySection projectId={id} />}

                {tab === 'Members' && <MembersSection members={members} state={membersState} />}

                {tab === 'Reports' && view === 'analytics' && (
                    <AnalyticsSection project={project} />
                )}
                {tab === 'Reports' && view === 'report' && id && <StatusReportView projectId={id} />}

                {tab === 'Tools' && view === 'ai' && id && <AIPilot projectId={id} projectName={project.name} />}
                {tab === 'Tools' && view === 'automations' && id && (
                    <div style={{ padding: 'var(--space-4)' }}>
                        <AutomationRules projectId={id} />
                    </div>
                )}
                {tab === 'Tools' && view === 'time' && <TimeTool />}
                {tab === 'Tools' && view === 'settings' && <ProjectSettings project={project} onProjectChange={setProject} />}
                {tab === 'Tools' && view === 'data' && <DataLifecycle project={project} onProjectChange={setProject} />}
            </section>

            {/* Add Member Modal */}
            <InviteMemberModal members={members} canRestrict={canRestrict} state={membersState} />
        </div>
    );
}

/** "owner/repo" for a repository URL; only an http(s) URL becomes a link. */
function repoLabel(url?: string): { label: string; href?: string } | null {
    if (!url) return null;
    try {
        const u = new URL(url);
        if (u.protocol !== 'https:' && u.protocol !== 'http:') return { label: url };
        const path = u.pathname.replace(/^\/+|\/+$/g, '').replace(/\.git$/, '');
        return { label: path || u.host, href: u.href };
    } catch {
        return { label: url };
    }
}
